import { useCallback, useEffect, useRef, useState } from 'react';
import { BADGES } from '../../utils/badges';

const KEY = (uid) => `padel-mvp-badges-celebrated:${uid}`;
const SETTLE_MS = 1500;

function readCelebrated(uid, memory) {
  try {
    const raw = window.localStorage.getItem(KEY(uid));
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (_) {
    return new Set(memory.current);
  }
}

function writeCelebrated(uid, ids, memory) {
  memory.current = [...ids];
  try {
    window.localStorage.setItem(KEY(uid), JSON.stringify([...ids]));
  } catch (_) {
    // Storage blocked: remembered for this launch only
  }
}

/**
 * Picks newly unlocked badges to celebrate once all the player's data is in and nothing
 * else needs their attention (no live match). Several at once show as one dialog for the
 * best badge, "plus N more". Each badge is celebrated once per device.
 */
export default function useAchievementAlerts({ uid, unlocked, ready }) {
  const [alert, setAlert] = useState(null); // { badge, extra }
  const memory = useRef([]);
  const unlockedKey = Object.keys(unlocked || {}).sort().join(',');

  useEffect(() => {
    if (!uid || !ready || alert) return undefined;
    // Wait for the numbers to stop moving (club data arriving in pieces) before deciding
    const timer = setTimeout(() => {
      const celebrated = readCelebrated(uid, memory);
      const fresh = BADGES.filter((b) => b.id in unlocked && !celebrated.has(b.id));
      if (fresh.length === 0) return;
      const best = [...fresh].sort(
        (a, b) => b.tierIndex - a.tierIndex || (unlocked[b.id] ?? 0) - (unlocked[a.id] ?? 0)
      )[0];
      writeCelebrated(uid, new Set([...celebrated, ...fresh.map((b) => b.id)]), memory);
      setAlert({ badge: best, extra: fresh.length - 1 });
    }, SETTLE_MS);
    return () => clearTimeout(timer);
    // unlockedKey stands in for `unlocked`, which is a new object on every recompute
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, ready, alert, unlockedKey]);

  const dismiss = useCallback(() => setAlert(null), []);
  return [alert, dismiss];
}
